import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileCheck2, ShieldCheck, Clock, Mail, MessageCircle, MapPin, Phone } from "lucide-react";
import { MaskLines, Reveal, Stagger, Item } from "../Motion";
import { Btn, Container, Eyebrow, ArrowLink } from "../Shared";
import { WA, PHONE } from "../Footer";
import { useLang, fmtDate } from "../../lib/lang";
import { CERT_DOCS, TESTIMONIALS } from "../../lib/routes";
import { api } from "../../lib/api";

const H2 = ({ children, light }) => <h2 className={`mt-6 font-display text-3xl font-light leading-[1.05] tracking-tight md:text-5xl ${light ? "text-white" : "text-[var(--ink)]"}`}><MaskLines inView lines={[children]} /></h2>;

export const Certified = () => {
  const { t, r, lang } = useLang();
  return (
    <section className="border-t border-[var(--line)] py-24 md:py-36" data-testid="certified-section">
      <Container className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal><Eyebrow>{t.cert.eyebrow}</Eyebrow></Reveal>
            <H2>{t.cert.title}</H2>
            <Reveal className="mt-8 overflow-hidden rounded-sm"><img src="/media/img/a5.jpg" alt="" loading="lazy" className="aspect-[5/4] w-full object-cover grayscale [clip-path:polygon(0_0,100%_0,100%_82%,82%_100%,0_100%)]" /></Reveal>
          </div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal className="text-lg leading-relaxed text-[var(--muted)]">{t.cert.text}</Reveal>
          <Stagger className="mt-10 space-y-0">
            {t.cert.points.map((p, i) => (
              <Item key={p} className="flex items-center gap-5 border-t border-[var(--line)] py-5 text-[var(--ink)]">
                <span className="font-mono text-xs text-[var(--teal-ink)]">0{i + 1}</span><span className="font-display text-xl">{p}</span>
              </Item>
            ))}
          </Stagger>
          <Reveal className="mt-12 rounded-sm bg-white p-7 md:p-9 border border-[var(--line)]">
            <p className="eyebrow text-[var(--muted)]">{t.cert.docs}</p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {CERT_DOCS[lang].map(([label, to]) => (
                <Link key={to} to={to} className="group flex items-center justify-between gap-3 rounded-sm border border-[var(--line)] px-4 py-3.5 text-sm text-[var(--ink)] transition-colors hover:border-[var(--ink)]" data-testid={`cert-doc-${to}`}>
                  <span className="flex items-center gap-3"><FileCheck2 className="h-4 w-4 text-[var(--teal-ink)]" />{label}</span><span className="transition-transform group-hover:translate-x-1">→</span>
                </Link>
              ))}
            </div>
            <p className="mt-5 text-sm text-[var(--muted)]">{t.cert.note}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Btn to={r("upload")} data-testid="certified-upload-btn">{t.cert.cta}</Btn>
              <Btn to={r("certified")} variant="ghost" icon="up" data-testid="certified-more-btn">{t.nav.certified}</Btn>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export const Interpreting = () => {
  const { t, r } = useLang();
  const links = ["consecutive", "interpreting", "online", "events"];
  return (
    <section className="relative overflow-hidden bg-[var(--ink)] py-24 text-white md:py-36 grain" data-testid="interpreting-section">
      <div className="pointer-events-none absolute inset-0 grid-lines" />
      <Container className="relative">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7"><Reveal><Eyebrow light>{t.interp.eyebrow}</Eyebrow></Reveal><H2 light>{t.interp.title}</H2></div>
          <Reveal className="self-end text-lg text-white/75 lg:col-span-4 lg:col-start-9">{t.interp.text}</Reveal>
        </div>
        <Stagger className="mt-16 grid border-t border-white/15 md:grid-cols-2 lg:grid-cols-4">
          {t.interp.modes.map(([title, d], i) => (
            <Item key={title} className="border-b border-white/15 md:[&:nth-child(odd)]:border-r lg:border-b-0 lg:border-r lg:last:border-r-0">
              <Link to={r(links[i])} className="group flex h-full flex-col justify-between gap-12 p-7 transition-colors duration-500 hover:bg-white/[0.04] md:p-8" data-testid={`interp-mode-${links[i]}`}>
                <span className="font-mono text-xs text-[#9FD3CD]">0{i + 1}</span>
                <div>
                  <h3 className="font-display text-2xl font-normal">{title}</h3>
                  <p className="mt-3 text-[0.95rem] text-white/75">{d}</p>
                  <span className="mt-6 inline-block h-px w-10 bg-[var(--teal)] transition-[width] duration-500 group-hover:w-20" />
                </div>
              </Link>
            </Item>
          ))}
        </Stagger>
        <Reveal className="mt-14"><Btn to={r("contact")} variant="teal" data-testid="interp-cta-btn">{t.interp.cta}</Btn></Reveal>
      </Container>
    </section>
  );
};

export const Process = () => {
  const { t } = useLang();
  return (
    <section className="py-24 md:py-36" data-testid="process-section">
      <Container>
        <Reveal><Eyebrow>{t.process.eyebrow}</Eyebrow></Reveal>
        <div className="max-w-3xl"><H2>{t.process.title}</H2></div>
        <div className="relative mt-16 grid gap-10 md:grid-cols-4 md:gap-6">
          <motion.div className="absolute left-0 right-0 top-[22px] hidden h-px origin-left bg-[var(--teal)] md:block" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1.8, ease: [0.65, 0, 0.35, 1] }} />
          {t.process.steps.map(([title, d], i) => (
            <Reveal key={title} delay={0.15 * i} className="relative">
              <span className="relative z-10 flex h-11 w-11 items-center justify-center rounded-full border border-[var(--ink)] bg-[var(--paper)] font-mono text-sm">{i + 1}</span>
              <h3 className="mt-6 font-display text-2xl text-[var(--ink)]">{title}</h3>
              <p className="mt-3 text-[var(--muted)]">{d}</p>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const Trust = () => {
  const { t, r } = useLang();
  return (
    <section className="border-t border-[var(--line)] bg-white py-24 md:py-36" data-testid="trust-section">
      <Container>
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5"><Reveal><Eyebrow>{t.trust.eyebrow}</Eyebrow></Reveal><H2>{t.trust.title}</H2>
            <Reveal className="mt-10"><p className="eyebrow text-[var(--muted)]">{t.trust.sectors}</p>
              <div className="mt-4 flex flex-wrap gap-2">{t.trust.sectorList.map((s) => <span key={s} className="rounded-full border border-[var(--line)] px-4 py-2 text-sm text-[var(--ink)]">{s}</span>)}</div>
            </Reveal>
          </div>
          <Stagger className="lg:col-span-7">
            {t.trust.reasons.map(([title, d], i) => (
              <Item key={title} className="grid grid-cols-[3rem_1fr] gap-4 border-t border-[var(--line)] py-6 md:grid-cols-[4rem_1fr_1.3fr]">
                <span className="font-mono text-xs text-[var(--teal-ink)] pt-1.5">0{i + 1}</span>
                <h3 className="font-display text-xl text-[var(--ink)]">{title}</h3>
                <p className="col-start-2 text-[var(--muted)] md:col-start-auto">{d}</p>
              </Item>
            ))}
          </Stagger>
        </div>
        <div className="mt-24">
          <Reveal className="flex items-end justify-between gap-6"><p className="font-display text-2xl text-[var(--ink)] md:text-3xl">{t.trust.testimonials}</p><ArrowLink to={r("testimonials")} data-testid="testimonials-link">{t.footer.testimonials}</ArrowLink></Reveal>
          <Stagger className="mt-10 grid gap-5 md:grid-cols-3">
            {TESTIMONIALS.map((q) => (
              <Item key={q.name} className="flex flex-col justify-between rounded-sm border border-[var(--line)] bg-[var(--paper)] p-7 md:p-8" data-testid="testimonial-card">
                <div><span className="font-display text-5xl leading-none text-[var(--teal)]">“</span><blockquote lang="en" className="mt-2 text-[1.02rem] leading-relaxed text-[var(--ink)]">{q.quote}</blockquote></div>
                <div className="mt-8 border-t border-[var(--line)] pt-4"><p className="font-medium text-[var(--ink)]">{q.name}</p><p className="text-sm text-[var(--muted)]">{q.role}{t.trust.original && lang_note(t)}</p></div>
              </Item>
            ))}
          </Stagger>
        </div>
      </Container>
    </section>
  );
};
const lang_note = (t) => (t.trust.original ? ` · ${t.trust.original}` : "");

export const AboutIntro = () => {
  const { t, r } = useLang();
  return (
    <section className="py-24 md:py-36" data-testid="about-section">
      <Container className="grid items-center gap-14 lg:grid-cols-12">
        <Reveal className="relative lg:col-span-5">
          <img src="/media/img/a3.jpg" alt="" loading="lazy" className="aspect-[4/5] w-full rounded-sm object-cover grayscale [clip-path:polygon(18%_0,100%_0,100%_100%,0_100%,0_18%)]" />
          <span className="absolute -bottom-6 -right-2 rounded-sm bg-[var(--ink)] px-6 py-5 font-display text-4xl font-light text-white md:-right-6">2016</span>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal><Eyebrow>{t.about.eyebrow}</Eyebrow></Reveal>
          <H2>{t.about.title}</H2>
          <Reveal className="mt-8 text-lg leading-relaxed text-[var(--muted)]">{t.about.text}</Reveal>
          <Reveal className="mt-10"><ArrowLink to={r("about")} data-testid="about-link">{t.about.cta}</ArrowLink></Reveal>
        </div>
      </Container>
    </section>
  );
};

export const PostCard = ({ p, lang, t }) => (
  <Link to={p.path} className="group flex h-full flex-col" data-testid="post-card">
    <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-[var(--paper-2)]">
      {p.featured_image ? (
        <img src={p.featured_image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
      ) : (
        <div className="flex h-full w-full items-end bg-[var(--ink)] p-6"><span className="font-display text-3xl font-light leading-tight text-white/90 line-clamp-3">{p.title}</span></div>
      )}
      {p.lang !== lang && <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 font-mono text-[0.7rem] uppercase text-[var(--ink)]">{p.lang}</span>}
    </div>
    <p className="mt-5 font-mono text-xs uppercase tracking-wider text-[var(--muted)]">{fmtDate(p.date, lang)}{p.categories?.[0] ? ` · ${p.categories[0]}` : ""}</p>
    <h3 className="mt-2 font-display text-xl leading-snug text-[var(--ink)] transition-colors group-hover:text-[var(--teal-ink)]">{p.title}</h3>
    {p.excerpt && <p className="mt-2 text-[0.95rem] text-[var(--muted)] line-clamp-2">{p.excerpt}</p>}
  </Link>
);

export const LatestPosts = () => {
  const { t, r, lang } = useLang();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/posts", { params: { lang: lang === "de" ? "de" : undefined, limit: 3 } })
      .then(async (res) => {
        let items = Array.isArray(res.data?.items) ? res.data.items : [];
        if (items.length < 3) { const all = await api.get("/posts", { params: { limit: 3 } }); items = [...items, ...(all.data?.items || [])].filter((v, i, a) => a.findIndex((x) => x.path === v.path) === i).slice(0, 3); }
        setPosts(items);
      })
      .catch(() => setPosts([]));
  }, [lang]);
  if (!posts.length) return null;
  return (
    <section className="border-t border-[var(--line)] py-24 md:py-36" data-testid="blog-section">
      <Container>
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl"><Reveal><Eyebrow>{t.blog.eyebrow}</Eyebrow></Reveal><H2>{t.blog.title}</H2></div>
          <Reveal><ArrowLink to={r("blog")} data-testid="blog-all-link">{t.blog.more}</ArrowLink></Reveal>
        </div>
        <Stagger className="mt-14 grid gap-10 md:grid-cols-3 md:gap-6">{posts.map((p) => <Item key={p.path}><PostCard p={p} lang={lang} t={t} /></Item>)}</Stagger>
      </Container>
    </section>
  );
};

export const UploadCta = () => {
  const { t, r } = useLang();
  const icons = [FileCheck2, Clock, ShieldCheck];
  return (
    <section className="px-3 pb-3 md:px-5 md:pb-5" data-testid="upload-cta-section">
      <div className="relative overflow-hidden rounded-sm bg-[var(--teal)] text-[var(--ink)]">
        <svg className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] opacity-25" viewBox="0 0 100 100" aria-hidden="true"><rect x="22" y="22" width="56" height="56" rx="6" transform="rotate(45 50 50)" fill="none" stroke="#12202B" strokeWidth="0.6" /><rect x="32" y="32" width="36" height="36" rx="4" transform="rotate(45 50 50)" fill="none" stroke="#12202B" strokeWidth="0.6" /></svg>
        <Container className="relative grid gap-12 py-20 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow flex items-center gap-3 text-[var(--ink)]"><span className="inline-block h-2 w-2 rotate-45 border border-[var(--ink)]" />{t.upcta.eyebrow}</p></Reveal>
            <h2 className="mt-6 font-display text-4xl font-light leading-[1.02] tracking-tight md:text-6xl"><MaskLines inView lines={[t.upcta.title]} /></h2>
            <Reveal className="mt-6 max-w-xl text-lg text-[var(--ink)]/85">{t.upcta.text}</Reveal>
          </div>
          <div className="flex flex-col justify-end gap-6 lg:col-span-4 lg:col-start-9">
            {t.upcta.points.map((p, i) => { const I = icons[i]; return <Reveal key={p} delay={i * 0.1} className="flex items-center gap-4 border-t border-[var(--ink)]/20 pt-4"><I className="h-5 w-5" />{p}</Reveal>; })}
            <Reveal delay={0.3}><Btn to={r("upload")} className="mt-4 w-full sm:w-auto" data-testid="upload-cta-btn">{t.upcta.cta}</Btn></Reveal>
          </div>
        </Container>
      </div>
    </section>
  );
};

export const ContactCta = () => {
  const { t, r } = useLang();
  return (
    <section className="py-24 md:py-36" data-testid="contact-cta-section">
      <Container className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7"><Reveal><Eyebrow>{t.ccta.eyebrow}</Eyebrow></Reveal>
          <h2 className="mt-6 font-display text-4xl font-light leading-[1.02] tracking-tight text-[var(--ink)] md:text-7xl"><MaskLines inView lines={[t.ccta.title]} /></h2>
          <Reveal className="mt-8 max-w-xl text-lg text-[var(--muted)]">{t.ccta.text}</Reveal>
          <Reveal className="mt-10 flex flex-wrap gap-3"><Btn to={r("contact")} data-testid="contact-cta-btn">{t.nav.contact}</Btn><Btn href={WA} target="_blank" rel="noopener noreferrer" variant="ghost" icon="up" data-testid="contact-cta-whatsapp">{t.ccta.whatsapp}</Btn></Reveal>
        </div>
        <Stagger className="self-end lg:col-span-4 lg:col-start-9">
          {[[Mail, "info@translinked.net", "mailto:info@translinked.net"], [Phone, PHONE, "tel:+491608493791"], [MessageCircle, "WhatsApp", WA], [MapPin, "Aegidiistr. 35, 48143 Münster", "https://www.openstreetmap.org/search?query=Aegidiistr.%2035%2C%2048143%20M%C3%BCnster"]].map(([I, label, href]) => (
            <Item key={label}><a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="group flex items-center gap-4 border-t border-[var(--line)] py-5 text-[var(--ink)]" data-testid={`contact-cta-${label.slice(0, 6)}`}><I className="h-5 w-5 text-[var(--teal-ink)]" /><span className="transition-transform duration-300 group-hover:translate-x-1">{label}</span></a></Item>
          ))}
        </Stagger>
      </Container>
    </section>
  );
};
